import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  Chip,
  CircularProgress, // CircularProgress eklendi
} from "@mui/material";
import { apiCall } from "../config/api";

const NotificationPage = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("normal");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [manualToken, setManualToken] = useState("");
  const [useManualToken, setUseManualToken] = useState(false);
  // Alıcı var mı? Hızlı test butonlarını bu koşulla pasifleştiriyoruz
  const hasRecipient =
    (useManualToken && manualToken.trim()) || (!useManualToken && selectedUser);

  // Kullanıcıları yükle
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await apiCall("/api/admin/users", {
          method: "GET",
        });
        setUsers(response.users || []);
      } catch (error) {
        console.error("Kullanıcılar yüklenirken hata:", error);
      }
    };

    fetchUsers();
  }, []);

  // FCM bildirim gönder
  const sendNotification = async () => {
    if (!title || !body || !category) {
      setMessage("Lütfen başlık, mesaj ve kategori alanlarını doldurun");
      setMessageType("error");
      return;
    }

    let fcmToken = "";

    if (useManualToken) {
      if (!manualToken.trim()) {
        setMessage("Lütfen FCM token giriniz");
        setMessageType("error");
        return;
      }
      fcmToken = manualToken.trim();
    } else {
      if (!selectedUser) {
        setMessage("Lütfen bir kullanıcı seçin");
        setMessageType("error");
        return;
      }

      const selectedUserData = users.find((user) => user.id === selectedUser);

      if (!selectedUserData?.fcmToken) {
        setMessage("Kullanıcının FCM token'ı bulunamadı");
        setMessageType("error");
        return;
      }

      fcmToken = selectedUserData.fcmToken;
    }

    setLoading(true);
    try {
      // Firebase Functions ile bildirim gönderimi
      const result = await apiCall("/api/send-notification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: fcmToken,
          title,
          body,
          category,
          priority,
        }),
      });

      if (result.success) {
        // Log artık backend (Functions) tarafında tutuluyor (announcement_logs)
        setMessage("Bildirim başarıyla gönderildi! (Log kaydı backend'de)");
        setMessageType("success");

        // Formu temizle
        setTitle("");
        setBody("");
        setCategory("");
        setPriority("normal");
        setSelectedUser("");
      } else {
        setMessage("Bildirim gönderilirken hata oluştu");
        setMessageType("error");
      }
    } catch (error) {
      console.error("Bildirim gönderme hatası:", error);
      setMessage("Bildirim gönderilirken hata oluştu");
      setMessageType("error");
    }
    setLoading(false);
  };

  // Test bildirimi gönder
  const sendTestNotification = async () => {
    // Alanları hazırla
    setTitle("🧪 Test Bildirimi");
    setBody("Bu bir test bildirimidir. Admin panelden gönderildi.");
    setCategory("system");
    setPriority("normal");

    // Alıcı hazırsa tek tıkla gönder, değilse kullanıcıyı bilgilendir
    if (!hasRecipient) {
      setMessage("Lütfen önce bir alıcı seçin veya manuel token girin.");
      setMessageType("info");
      return;
    }
    await sendNotification();
  };

  // Beğeni test bildirimi
  const sendLikeTestNotification = async () => {
    // Alanları hazırla
    setTitle("❤️ Pin Beğenildi");
    setBody("Birileri pin'inizi beğendi!");
    setCategory("social");
    setPriority("normal");

    // Alıcı hazırsa tek tıkla gönder, değilse kullanıcıyı bilgilendir
    if (!hasRecipient) {
      setMessage("Lütfen önce bir alıcı seçin veya manuel token girin.");
      setMessageType("info");
      return;
    }
    await sendNotification();
  };

  // Topic broadcast (Genel Duyuru)
  const [topicTitle, setTopicTitle] = useState("");
  const [topicBody, setTopicBody] = useState("");
  const [topic, setTopic] = useState("app_updates");
  const [topicLoading, setTopicLoading] = useState(false);

  const handleTopicBroadcast = async () => {
    if (!topicTitle || !topicBody) {
      setMessage("Lütfen duyuru başlığı ve içeriğini giriniz");
      setMessageType("error");
      return;
    }
    setTopicLoading(true);
    try {
      const result = await apiCall("/api/notifications/topic-broadcast", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          topic,
          title: topicTitle,
          body: topicBody,
          data: { category: "system", priority: "normal" },
        }),
      });
      if (result.success) {
        setMessage("Genel duyuru gönderildi!");
        setMessageType("success");
        setTopicTitle("");
        setTopicBody("");
      } else {
        setMessage(result.error || "Duyuru gönderilemedi");
        setMessageType("error");
      }
    } catch (e) {
      setMessage("Duyuru gönderimi sırasında hata oluştu");
      setMessageType("error");
    }
    setTopicLoading(false);
  };

  // Herkese bildirim gönder (Send to All)
  const [sendToAllTitle, setSendToAllTitle] = useState("");
  const [sendToAllBody, setSendToAllBody] = useState("");
  const [sendToAllCategory, setSendToAllCategory] = useState("system");
  const [sendToAllPriority, setSendToAllPriority] = useState("normal");
  const [sendToAllLoading, setSendToAllLoading] = useState(false);

  const handleSendToAll = async () => {
    if (!sendToAllTitle || !sendToAllBody) {
      setMessage("Lütfen başlık ve mesaj giriniz");
      setMessageType("error");
      return;
    }
    setSendToAllLoading(true);
    try {
      const result = await apiCall("/api/notifications/send-to-all", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: sendToAllTitle,
          body: sendToAllBody,
          category: sendToAllCategory,
          priority: sendToAllPriority,
        }),
      });
      if (result.success) {
        const report = result.report || {};
        setMessage(
          `Herkese bildirim gönderildi! Toplam: ${
            report.total || 0
          }, Başarılı: ${report.success || 0}, Hata: ${report.failure || 0}`
        );
        setMessageType("success");
        setSendToAllTitle("");
        setSendToAllBody("");
        setSendToAllCategory("system");
        setSendToAllPriority("normal");
      } else {
        setMessage(result.error || result.message || "Bildirim gönderilemedi");
        setMessageType("error");
      }
    } catch (e) {
      setMessage(
        "Herkese bildirim gönderimi sırasında hata oluştu: " + e.message
      );
      setMessageType("error");
    }
    setSendToAllLoading(false);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        📱 Bildirim Yönetimi
      </Typography>

      {message && (
        <Alert severity={messageType} sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Tek Bildirim Gönder + Hızlı Test (birleştirilmiş) */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              📤 Tek Kişiye Bildirim Gönder
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Kullanıcı Seç</InputLabel>
                <Select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  label="Kullanıcı Seç"
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.displayName || user.email}
                      {user.fcmToken ? (
                        <Chip
                          label="FCM ✅"
                          size="small"
                          color="success"
                          sx={{ ml: 1 }}
                        />
                      ) : (
                        <Chip
                          label="FCM ❌"
                          size="small"
                          color="error"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Manuel Token Seçeneği */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  variant={useManualToken ? "contained" : "outlined"}
                  onClick={() => setUseManualToken(!useManualToken)}
                  size="small"
                >
                  {useManualToken
                    ? "📱 Manuel Token Aktif"
                    : "📱 Manuel Token Kullan"}
                </Button>
              </Box>

              {useManualToken && (
                <TextField
                  fullWidth
                  label="FCM Token"
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value)}
                  placeholder="FCM token'ı buraya yapıştırın"
                  multiline
                  rows={3}
                  helperText="Telefonunuzdan kopyaladığınız FCM token'ı buraya yapıştırın"
                />
              )}

              <TextField
                fullWidth
                label="Başlık"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Bildirim başlığı"
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Mesaj"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Bildirim mesajı"
              />

              <FormControl fullWidth>
                <InputLabel>Kategori</InputLabel>
                <Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  label="Kategori"
                >
                  <MenuItem value="social">📱 Sosyal (Beğeni, Yorum)</MenuItem>
                  <MenuItem value="system">
                    ⚙️ Sistem (Güvenlik, Güncelleme)
                  </MenuItem>
                  <MenuItem value="location">📍 Konum (Yakın Pin)</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Öncelik</InputLabel>
                <Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  label="Öncelik"
                >
                  <MenuItem value="low">🔵 Düşük</MenuItem>
                  <MenuItem value="normal">🟡 Normal</MenuItem>
                  <MenuItem value="high">🟠 Yüksek</MenuItem>
                  <MenuItem value="urgent">🔴 Acil</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={sendNotification}
                disabled={loading}
                size="large"
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  "📤 Bildirim Gönder"
                )}
              </Button>

              {/* Hızlı Test (pasif koşullu) */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={sendTestNotification}
                  disabled={!hasRecipient || loading}
                >
                  🧪 Test Bildirimi (hemen gönder)
                </Button>
                <Button
                  variant="outlined"
                  onClick={sendLikeTestNotification}
                  disabled={!hasRecipient || loading}
                >
                  ❤️ Beğeni Bildirimi (hemen gönder)
                </Button>
                {!hasRecipient && (
                  <Typography variant="caption" color="text.secondary">
                    Hızlı test butonları için önce bir alıcı seçin veya manuel
                    token girin.
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Genel Duyuru (Topic) */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              📣 Genel Duyuru (Topic Yayını)
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Konu</InputLabel>
              <Select
                value={topic}
                label="Konu"
                onChange={(e) => setTopic(e.target.value)}
              >
                <MenuItem value="app_updates">Uygulama Güncellemeleri</MenuItem>
                <MenuItem value="maintenance_notifications">
                  Bakım Bildirimleri
                </MenuItem>
                <MenuItem value="security_alerts">Güvenlik Uyarıları</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Duyuru Başlığı"
              value={topicTitle}
              onChange={(e) => setTopicTitle(e.target.value)}
              placeholder="Örn. Yeni sürüm yayında"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Duyuru İçeriği"
              value={topicBody}
              onChange={(e) => setTopicBody(e.target.value)}
              placeholder="Detaylı açıklama"
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleTopicBroadcast}
                disabled={topicLoading}
                fullWidth
              >
                {topicLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  "📤 Duyuru Gönder (Topic)"
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={async () => {
                  setTopicLoading(true);
                  const r = await apiCall("/api/notifications/subscribe-all", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ topic }),
                  });
                  if (r.success) {
                    setMessage(
                      `Tüm kullanıcılar ${topic} topiğine abone edildi. Toplam: ${
                        r.total || 0
                      }, Başarılı: ${r.subscribed || 0}`
                    );
                    setMessageType("success");
                  } else {
                    setMessage(
                      r.error || r.message || "Abonelik işlemi başarısız"
                    );
                    setMessageType("error");
                  }
                  setTopicLoading(false);
                }}
                disabled={topicLoading}
              >
                {topicLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  "👥 Tüm Kullanıcıları Bu Topiğe Abone Et"
                )}
              </Button>
            </Box>
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 1, color: "text.secondary" }}
            >
              Not: Bu duyuru, sadece ilgili konuyu Ayarlar’da açık tutan
              kullanıcılara gider.
            </Typography>
          </Paper>
        </Grid>

        {/* Herkese Bildirim Gönder */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              🌍 Herkese Bildirim Gönder
            </Typography>
            <TextField
              fullWidth
              label="Başlık"
              value={sendToAllTitle}
              onChange={(e) => setSendToAllTitle(e.target.value)}
              placeholder="Bildirim başlığı"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Mesaj"
              value={sendToAllBody}
              onChange={(e) => setSendToAllBody(e.target.value)}
              placeholder="Bildirim mesajı"
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Kategori</InputLabel>
              <Select
                value={sendToAllCategory}
                onChange={(e) => setSendToAllCategory(e.target.value)}
                label="Kategori"
              >
                <MenuItem value="social">📱 Sosyal</MenuItem>
                <MenuItem value="system">⚙️ Sistem</MenuItem>
                <MenuItem value="location">📍 Konum</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Öncelik</InputLabel>
              <Select
                value={sendToAllPriority}
                onChange={(e) => setSendToAllPriority(e.target.value)}
                label="Öncelik"
              >
                <MenuItem value="low">🔵 Düşük</MenuItem>
                <MenuItem value="normal">🟡 Normal</MenuItem>
                <MenuItem value="high">🟠 Yüksek</MenuItem>
                <MenuItem value="urgent">🔴 Acil</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="warning"
              onClick={handleSendToAll}
              disabled={sendToAllLoading}
              fullWidth
            >
              {sendToAllLoading ? (
                <CircularProgress size={24} />
              ) : (
                "🌍 Herkese Gönder"
              )}
            </Button>
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 1, color: "text.secondary" }}
            >
              ⚠️ DİKKAT: Bu bildirim TÜM kullanıcılara gönderilir ve bildirim
              geçmişine kaydedilir!
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default NotificationPage;
